// Sprint UI-7b — resizable sidebar width, persisted to localStorage.
//
// Matches Cursor/VSCode: a 4 px hover-to-resize handle on the right edge
// of the sidebar column. Drag to resize; width persists across reloads.
//
// Clamp: min 220 px, max 400 px. Default 280 px. Width below min collapses
// to min (we don't "fold" the sidebar here — that's a future sprint).
//
// Storage key: `asoasis:sidebar-width`. A bad value (NaN, out of range,
// parseInt failure) falls back to default and the bad key is overwritten
// on next write. SSR-safe: returns default on first render when
// `window` is absent.

import { useCallback, useEffect, useRef, useState } from 'react';

const STORAGE_KEY = 'asoasis:sidebar-width';
export const SIDEBAR_MIN_WIDTH = 220;
export const SIDEBAR_MAX_WIDTH = 400;
export const SIDEBAR_DEFAULT_WIDTH = 280;

function readStored(): number {
  if (typeof window === 'undefined') return SIDEBAR_DEFAULT_WIDTH;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === null) return SIDEBAR_DEFAULT_WIDTH;
    const n = parseInt(raw, 10);
    if (!Number.isFinite(n)) return SIDEBAR_DEFAULT_WIDTH;
    return clamp(n);
  } catch {
    return SIDEBAR_DEFAULT_WIDTH;
  }
}

function clamp(n: number): number {
  if (n < SIDEBAR_MIN_WIDTH) return SIDEBAR_MIN_WIDTH;
  if (n > SIDEBAR_MAX_WIDTH) return SIDEBAR_MAX_WIDTH;
  return Math.round(n);
}

export interface UseResizableSidebar {
  width: number;
  isResizing: boolean;
  /** Attach to the resize handle's onPointerDown. */
  startResizing: (e: React.PointerEvent) => void;
}

export function useResizableSidebar(): UseResizableSidebar {
  const [width, setWidth] = useState<number>(() => readStored());
  const [isResizing, setIsResizing] = useState(false);
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);

  // Persist on every committed width change. Ignoring writes during active
  // drags keeps localStorage quiet (50+ writes/s would be wasteful); the
  // final value lands on pointerup.
  useEffect(() => {
    if (isResizing) return;
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(STORAGE_KEY, String(width));
    } catch {
      // Quota / disabled — ignore; the next successful write repopulates.
    }
  }, [width, isResizing]);

  const startResizing = useCallback((e: React.PointerEvent) => {
    e.preventDefault();
    setIsResizing(true);
    startXRef.current = e.clientX;
    startWidthRef.current = width;

    function onMove(ev: PointerEvent) {
      const dx = ev.clientX - startXRef.current;
      setWidth(clamp(startWidthRef.current + dx));
    }
    function onUp() {
      setIsResizing(false);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    }
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
  }, [width]);

  return { width, isResizing, startResizing };
}
